export type JobStatus =
  | "draft"
  | "ready"
  | "queued"
  | "preparing"
  | "rendering"
  | "uploading"
  | "completed"
  | "failed"
  | "interrupted";

export const UNSUPPORTED_PROMPT_MARKER = "[지원되지 않는 내용 — 직접 수정 필요]";

export interface ReferenceImage {
  id: string;
  name: string;
  type: string;
  size: number;
  position: number;
  tag: string;
  url: string;
}

export interface Job {
  id: string;
  name: string;
  prompt: string;
  profile: string;
  width: number;
  height: number;
  frames: number;
  seed: number;
  status: JobStatus;
  progress: number;
  stage: string | null;
  reference_name: string;
  reference_kind: "image" | "video";
  reference_intent: ReferenceIntent;
  reference_duration: number | null;
  reference_width: number | null;
  reference_height: number | null;
  reference_url: string;
  reference_images: ReferenceImage[];
  result_url: string | null;
  result_name: string | null;
  result_size: number | null;
  result_expires_at: number | null;
  error: string | null;
  batch_id: string | null;
  execution_provider: "vast" | "windows" | "runpod" | "floyo" | null;
  execution_started_at: number | null;
  generation_started_at: number | null;
  generation_seconds: number | null;
  total_seconds: number | null;
  production_group_id: string | null;
  shot_index: number | null;
  shot_count: number | null;
  target_duration_seconds: number | null;
  continuity_source: "original_reference" | "previous_result_last_frame";
  continuity_parent_job_id: string | null;
  shot_duration_seconds: number | null;
  production_group: ProductionGroup | null;
  created_at: number;
  updated_at: number;
  completed_at: number | null;
  prompt_rewrite: PromptRewrite;
  realism_upgrade: RealismUpgrade;
}

export type ReferenceIntent = "subject_replacement" | "reference_generation";

export interface ProductionGroup {
  id: string;
  title: string | null;
  status: "draft" | "requested" | "processing" | "completed" | "failed";
  shot_count: number;
  target_duration_seconds: number;
  result_name: string | null;
  result_size: number | null;
  result_expires_at: number | null;
  result_url: string | null;
  error: string | null;
  completed_at: number | null;
  continuity_mode: "independent" | "previous_result_last_frame";
}

export interface JobResult {
  id: string;
  job_id: string;
  name: string;
  size: number | null;
  expires_at: number | null;
  available: boolean;
  execution_provider: "vast" | "windows" | "runpod" | "floyo" | null;
  prompt: string;
  profile: string;
  width: number | null;
  height: number | null;
  frames: number | null;
  seed: number | null;
  created_at: number;
  generation_seconds: number | null;
  total_seconds: number | null;
  reference_audit?: Ref2VAReferenceAudit | null;
  postprocess: PostprocessAdvice;
  motion_fix: MotionFix;
  video_url: string;
  download_url: string;
}

export type SocialProvider = "instagram" | "tiktok";

export interface SocialConnection {
  id: string;
  provider: SocialProvider;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  scopes: string[];
  connected_at: number;
  token_expires_at: number | null;
}

export interface SocialAccounts {
  providers: Record<SocialProvider, { configured: boolean; reason: string | null }>;
  connections: SocialConnection[];
}

export interface SocialDraft {
  hook: string;
  cover_title: string;
  instagram: { caption: string; hashtags: string[]; audio_name: string };
  tiktok: { caption: string; hashtags: string[] };
  music: {
    mood: string;
    search_terms: string[];
    recommendation: "original_audio" | "tiktok_edit";
    reason: string;
  };
}

export interface TikTokCreatorInfo {
  username: string;
  nickname: string;
  avatar_url: string | null;
  privacy_level_options: string[];
  comment_disabled: boolean;
  duet_disabled: boolean;
  stitch_disabled: boolean;
  max_video_post_duration_sec: number;
}

export interface SocialPublish {
  id: string;
  result_id: string;
  connection_id: string;
  provider: SocialProvider;
  mode: "direct" | "draft";
  status: "processing" | "published" | "draft_sent" | "failed";
  stage: string | null;
  caption: string;
  cover_timestamp_ms: number | null;
  media_variant: "original" | "motion_fix";
  platform_post_id: string | null;
  permalink: string | null;
  error: string | null;
  username: string | null;
  display_name: string | null;
  created_at: number;
  updated_at: number;
  completed_at: number | null;
}

export interface SocialPublishInput {
  client_request_id: string;
  connection_id: string;
  provider: SocialProvider;
  mode: "direct" | "draft";
  caption: string;
  hashtags: string[];
  cover_timestamp_ms: number;
  media_variant: "original" | "motion_fix";
  audio_name?: string;
  privacy_level?: string;
  allow_comment?: boolean;
  allow_duet?: boolean;
  allow_stitch?: boolean;
  own_brand?: boolean;
  branded_content?: boolean;
  music_usage_confirmed?: boolean;
}

export interface Ref2VAReferenceAudit {
  passed: boolean;
  direct_edit_required?: boolean;
  duration_delta_seconds?: number;
  frame_delta?: number | null;
  source_hard_cuts_seconds?: number[];
  result_hard_cuts_seconds?: number[];
  checks?: {
    fps_match?: boolean;
    duration_match?: boolean;
    frame_count_match?: boolean;
    hard_cut_count_match?: boolean;
    hard_cut_timing_match?: boolean;
  };
}

export type PostprocessStatus = "requested" | "processing" | "completed" | "failed" | null;

export interface PostprocessAdvice {
  status: PostprocessStatus;
  summary: string | null;
  verdict: "publish" | "postprocess" | "rerender" | null;
  actions: string[];
  prompt: string | null;
  rerender_prompt_appendix: string | null;
  error: string | null;
  requested_at: number | null;
  updated_at: number | null;
  speed_factor: 1 | 1.1 | 1.2 | 1.35 | null;
}

export type MotionFixStatus = "requested" | "processing" | "completed" | "failed" | null;

export interface MotionFix {
  status: MotionFixStatus;
  factor: number | null;
  size: number | null;
  expires_at: number | null;
  available: boolean;
  error: string | null;
  requested_at: number | null;
  updated_at: number | null;
  video_url: string | null;
  download_url: string | null;
}

export type PromptRewriteStatus = "requested" | "processing" | "completed" | "failed" | null;

export interface PromptRewrite {
  status: PromptRewriteStatus;
  input_prompt: string | null;
  output_prompt: string | null;
  notes: string[];
  error: string | null;
  requested_at: number | null;
  updated_at: number | null;
}

export type RealismUpgradeStatus = "requested" | "processing" | "applied" | "failed" | null;

export interface RealismUpgrade {
  status: RealismUpgradeStatus;
  input_prompt: string | null;
  output_prompt: string | null;
  changes: string[];
  warnings: string[];
  score_before: number | null;
  score_after: number | null;
  error: string | null;
  requested_at: number | null;
  updated_at: number | null;
}

export interface DashboardStatus {
  bridge: {
    online: boolean;
    heartbeat_at: number | null;
    version: string | null;
    host_label: string | null;
  };
  runtime: {
    active: boolean;
    provider: "vast" | "runpod";
    status: string;
    ready: boolean;
    gpu_label: string;
    gpu_count: number;
    vram_gb: number | null;
    disk_gb: number | null;
    hourly_usd: number | null;
    gpu_util_percent: number | null;
    started_at: number;
    observed_at: number | null;
    stale: boolean;
  } | null;
  batch: {
    id: string;
    status: string;
    job_count: number;
    current_job_id: string | null;
    progress: number;
    requested_at: number;
  } | null;
  windows_worker: {
    worker_id: string;
    worker_version: string;
    state: string;
    comfy_ready: boolean;
    online: boolean;
    heartbeat_at: number;
  } | null;
  counts: {
    ready: number;
    active: number;
    waiting: number;
    windows_candidates: number;
    windows_active: number;
    total: number;
    realism_pending: number;
    realism_candidates: number;
  };
}

export interface JobDraft {
  name: string;
  prompt: string;
  profile: string;
  width: number;
  height: number;
  frames: number;
  seed: number;
  reference_intent: ReferenceIntent;
}
